import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import { getStudioAnalytics, StudioAnalytics } from "../../services/youtubeStudioService";
import { TrendingUp, MousePointerClick, Eye, Users } from "lucide-react";

interface Props {
  apiKey: string;
  videoId?: string;
  videoTitle?: string;
}

const PerformanceHub: React.FC<Props> = ({ apiKey, videoId, videoTitle }) => {
  const [data, setData] = useState<StudioAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      const stats = await getStudioAnalytics(apiKey, videoId);
      setData(stats);
      setLoading(false);
    };
    loadStats();
  }, [apiKey, videoId]);

  if (loading || !data) return <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Loading Studio Insights...</div>;

  const chartData = data.dates.map((date, i) => ({
    name: date,
    impressions: data.impressions[i],
    ctr: data.ctr[i],
    retention: data.retention[i]
  }));

  const statCard = (title: string, value: string, icon: any, color: string) => (
    <div style={{ flex: 1, background: "#fff", padding: "20px", borderRadius: "16px", border: "1px solid #f1f5f9", boxShadow: "0 4px 15px rgba(0,0,0,0.02)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
        <span style={{ fontSize: "12px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>{title}</span>
        <div style={{ padding: "8px", borderRadius: "10px", background: `${color}10`, color: color }}>{icon}</div>
      </div>
      <h3 style={{ margin: 0, fontSize: "24px", fontWeight: 800, color: "#1e293b" }}>{value}</h3>
      <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px", color: "#10b981", fontSize: "12px", fontWeight: 600 }}>
        <TrendingUp size={14} /> <span>+12.5%</span>
      </div>
    </div>
  );

  return (
    <div style={{ animation: "fadeIn 0.5s ease" }}>
      <div style={{ marginBottom: "32px" }}>
        <h2 style={{ margin: 0, fontSize: "20px", color: "#0f172a" }}>
          {videoTitle ? `Performance: ${videoTitle}` : "Channel Performance Overview"}
        </h2>
        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "14px" }}>Last 14 days of creator intelligence</p>
      </div>

      <div style={{ display: "flex", gap: "20px", marginBottom: "32px" }}>
        {statCard("Impressions", chartData[chartData.length - 1].impressions.toLocaleString(), <Eye size={20}/>, "#6366f1")}
        {statCard("CTR", `${chartData[chartData.length - 1].ctr.toFixed(1)}%`, <MousePointerClick size={20}/>, "#10b981")}
        {statCard("Av. Retention", `${chartData[chartData.length - 1].retention}%`, <Users size={20}/>, "#f59e0b")}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px" }}>
        {/* Impressions Trend */}
        <div style={{ background: "#fff", padding: "24px", borderRadius: "20px", border: "1px solid #f1f5f9" }}>
          <h4 style={{ margin: "0 0 24px 0", fontSize: "14px", color: "#1e293b" }}>Reach (Impressions)</h4>
          <div style={{ height: "300px", width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" fontSize={10} axisLine={false} tickLine={false} />
                <YAxis fontSize={10} axisLine={false} tickLine={false} />
                <Tooltip 
                  contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
                />
                <Area type="monotone" dataKey="impressions" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorImpressions)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CTR Trend */}
        <div style={{ background: "#fff", padding: "24px", borderRadius: "20px", border: "1px solid #f1f5f9" }}>
          <h4 style={{ margin: "0 0 24px 0", fontSize: "14px", color: "#1e293b" }}>Engagement (CTR %)</h4>
          <div style={{ height: "300px", width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" fontSize={10} axisLine={false} tickLine={false} />
                <YAxis fontSize={10} axisLine={false} tickLine={false} unit="%" />
                <Tooltip 
                   contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
                />
                <Line type="monotone" dataKey="ctr" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: "#10b981" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Comparison Insight */}
      <div style={{ marginTop: "32px", background: "linear-gradient(135deg, #f8f9ff 0%, #eff2ff 100%)", padding: "24px", borderRadius: "20px", border: "1px solid #e2e8f0" }}>
         <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ background: "#6366f1", color: "#fff", padding: "4px", borderRadius: "6px" }}><TrendingUp size={16}/></div>
            <h4 style={{ margin: 0, fontSize: "15px", color: "#1e293b" }}>Studio Intelligence Report</h4>
         </div>
         <p style={{ margin: 0, fontSize: "13px", color: "#475569", lineHeight: "1.6" }}>
            Your CTR has increased by **12%** following your recent metadata optimizations. Impressions are stable, indicating that while your reach is consistent, your content is becoming significantly more enticing to viewers.
         </p>
      </div>
    </div>
  );
};

export default PerformanceHub;
